"use client";

import React, { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { jwtDecode } from "jwt-decode";
import { Icon } from "@iconify/react";
import Image from "next/image";

import { getTimeAheadInHrs, getTimeDifference } from "@/utils/formatDate";
import { getNumberSuffix } from "@/utils/formatIsoDate";
import useScreenWidth from "@/hooks/useScreenWidth";
import styles from "./test-link.module.scss";
import services from "@/store/services";
import Components from "@/components";
import assets from "@/assets/images";
import apis from "@/api";

const aboutTestPlatform = [
  {
    id: 1,
    point:
      "AI-Powered Camera Proctoring for real-time monitoring and fraud detection.",
  },
  {
    id: 2,
    point:
      "Fraud detection with face recognition, audio monitoring, and browser restrictions.",
  },
  {
    id: 3,
    point:
      "Secure environment with encrypted data, multi-layer authentication, and device lockdown.",
  },
  {
    id: 4,
    point:
      "Post-test analysis with detailed behavior reports and reviewable recordings.",
  },
  {
    id: 5,
    point:
      "Customizable test settings for time limits, randomization, and security features.",
  },
];

const SecondScreen = (props) => {
  const {
    eligibilityCrietria,
    firstRender,
    setFirstRender,
    isLoading,
    prevResult,
    isEvaluated,
    aproxEvalTime,
    pendingContents,
    testTime,
    cooledDown,
  } = props;
  const [coolTime, setCoolTime] = useState(0);

  useEffect(() => {
    if (testTime) {
      setCoolTime(
        getTimeDifference(
          new Date().toISOString(),
          new Date(getTimeAheadInHrs(testTime, 1)).toISOString()
        )
      );
    }
  }, [cooledDown, testTime]);

  const formatIncompleteContent = (data) => {
    const position = data.position;
    const msg = `${position}${getNumberSuffix(position)} ${data.type} is incomplete`;
    return msg;
  };

  useEffect(() => {
    if (firstRender) {
      setFirstRender(false);
    }
  }, []);

  return (
    <article className={styles["second-screen"]}>
      <div className={styles["img-container"]}>
        <Image
          src={assets.TestEligibility}
          alt="test-image"
          width={400}
          height={400}
        />
      </div>
      <div className={styles["second-container"]}>
        {pendingContents.length > 0 ? (
          <div className={styles["contents"]}>
            <h2 className={styles.title}>Test Eligibility</h2>

            <div className={styles.eligibility}>
              {isLoading ? (
                <Icon
                  icon="line-md:loading-loop"
                  className={styles.loadingIcon}
                />
              ) : (
                <Icon
                  icon={"ic:round-close"}
                  className={`${styles.icon} ${styles["corssed"]}`}
                />
              )}
              <p>All stage contents completed</p>
            </div>
            <div className={styles.eligibility}>
              {isLoading ? (
                <Icon
                  icon="line-md:loading-loop"
                  className={styles.loadingIcon}
                />
              ) : (
                <Icon
                  icon={"fluent-emoji-flat:page-facing-up"}
                  className={`${styles.icon} ${styles["corssed"]}`}
                />
              )}
              <p>Contents not completed:</p>
            </div>
            {pendingContents.map((item, i) => (
              <div
                key={i}
                className={styles.eligibility}
                style={{ paddingLeft: "20px" }}
              >
                <p className={styles.incomplete}>
                  <Icon
                    icon={"radix-icons:dot"}
                    className={`${styles.icon} ${styles["corssed"]}`}
                  />
                  {formatIncompleteContent(item)}
                </p>
              </div>
            ))}
          </div>
        ) : (
          <>
            {eligibilityCrietria.length > 0 && (
              <div className={styles["contents"]}>
                <h2 className={styles.title}>Test Eligibility</h2>
                {eligibilityCrietria?.map((item, i) => (
                  <div className={styles.eligibility} key={i}>
                    {isLoading ? (
                      <Icon
                        icon="line-md:loading-loop"
                        className={styles.loadingIcon}
                      />
                    ) : (
                      <Icon
                        icon={item.status ? "icons8:checked" : "ic:round-close"}
                        className={`${styles.icon} ${item.status ? styles["checked"] : styles["corssed"]}`}
                      />
                    )}

                    <p>{item.data}</p>
                  </div>
                ))}
              </div>
            )}
          </>
        )}

        <div className={styles["contents"]}>
          {prevResult ? (
            <>
              {isEvaluated ? (
                <h2 className={styles.title}>📝 Test Summary</h2>
              ) : (
                <h2 className={styles.title}>Test Evaluation Status</h2>
              )}
              {isEvaluated ? (
                <>
                  <div className={styles.eligibility}>
                    <p>Total Test Marks: {prevResult.totalScore}</p>
                  </div>
                  <div className={styles.eligibility}>
                    <p>
                      Best Score (out of all attempts):{" "}
                      {prevResult.obtainedScore}
                    </p>
                  </div>
                  <div className={styles.eligibility}>
                    <p>Attempts Used: {prevResult.attempts} / 3</p>
                  </div>
                  {prevResult.obtainedScore / prevResult.totalScore > 0.5 ? (
                    <>
                      <div className={styles.eligibility}>
                        <p>🥳 Congragulations! you have cleared the stest</p>
                      </div>
                      <div className={styles.eligibility}>
                        <p>✅ You are now eligible to start the next stage</p>
                      </div>
                    </>
                  ) : prevResult.attempts < 3 ? (
                    <>
                      <div className={styles.eligibility}>
                        <p>
                          ⚠️ Note: If your score isn't updated, it means it's
                          lower than your best score.
                        </p>
                      </div>
                      <div className={styles.eligibility}>
                        <p>
                          👉 Make sure to review the content thoroughly before
                          your final attempt.
                        </p>
                      </div>

                      {!cooledDown ? (
                        coolTime > 0 ? (
                          <div className={styles.eligibility}>
                            <p className={styles.warning}>
                              You may re-attempt the test in {coolTime} minutes.
                              Thank you for your patience.
                            </p>
                          </div>
                        ) : (
                          <div>
                            <p className={styles.warning}>
                              You may re-attempt the test in sometime. We
                              appreciate your patience. Please contact support
                              you are unable to re-attempt the test.
                            </p>
                          </div>
                        )
                      ) : (
                        <div className={styles.eligibility}>
                          <p className={styles.warning}>
                            You have {3 - prevResult.attempts} final attempt
                            remaining to score more than 50% to clear this
                            stage.
                          </p>
                        </div>
                      )}
                    </>
                  ) : (
                    <>
                      <div
                        className={styles.eligibility}
                        style={{ flexDirection: "column" }}
                      >
                        <p>❗ You've used all your test attempts.</p>
                        <p>
                          For further assistance, please reach out to our
                          support team.
                        </p>
                      </div>
                      <div
                        className={styles.eligibility}
                        style={{ flexDirection: "column" }}
                      >
                        <p>➡️ To contact support:</p>
                        <p>{`Please navigate to your Course batch → Help & Support and raise a query for a Test Reattempt`}</p>
                        <p>Our team will respond within 24 hours.</p>
                      </div>
                    </>
                  )}

                  {/* {prevResult.obtainedScore / prevResult.totalScore > 0.5 ? (
                    <div className={styles.eligibility}>
                      <p className={styles.success}>
                        You have successfully cleared the test
                      </p>
                    </div>
                  ) : (
                    <div className={styles.eligibility}>
                      {3 - prevResult.attempts > 0 ? (
                        <p className={styles.warning}>
                          You have not cleared the test, you have{" "}
                          {3 - prevResult.attempts} more attempts to clear it!
                        </p>
                      ) : (
                        <p className={styles.warning}>
                          You have exhausted your attempts, please contact
                          support for further assistance
                        </p>
                      )}
                    </div>
                  )} */}
                </>
              ) : (
                <div className={styles.eligibility}>
                  <p>
                    The evaluation of your test attempt is expected to be
                    completed by {aproxEvalTime}. We appreciate your patience.
                  </p>
                </div>
              )}
            </>
          ) : null}
        </div>
        {/* <div className={styles["contents"]}>
          <div className={styles.eligibility}>
            <p className={styles.timer}>
              45 mins more before you can re-attempt the test
            </p>
          </div>
        </div> */}
      </div>
    </article>
  );
};

const WarningScreen = (props) => {
  const { marksStats } = props;

  const avgPercentage = marksStats[0]?.totalMarks
    ? (marksStats[0]?.avgMarks / marksStats[0]?.totalMarks) * 100
    : 0;

  return (
    <section className={styles["warning-container"]}>
      <div className={styles["img-container"]}>
        <Image
          src={assets.LastAttempt}
          alt="test-image"
          width={400}
          height={400}
        />
      </div>
      <div className={styles["right"]}>
        <h3>Attention: Final attempt for the test</h3>
        <ul>
          <li>
            Your current best score is{" "}
            <strong>
              {marksStats[0]?.currBestMarks}/{marksStats[0]?.totalMarks}
            </strong>
            .
          </li>
        </ul>
        <p>
          {avgPercentage >= 50
            ? "Great job! You're on track to earn your certificate. Keep up the good work in this final attempt!"
            : "You're almost there! Improve your score in this attempt to qualify for the certificate."}
        </p>
      </div>
    </section>
  );
};

function TestLinkModals(props) {
  const { content, marksStats, attemptsCount, closeHandler } = props;
  const { testInfo, courseInfo } = useSelector((state) => state.video);

  const [eligibilityCrietria, setEligibilityCrietria] = useState([
    { status: false, loading: false, data: "Checking eligibility" },
  ]);
  const [prevResult, setPrevResult] = useState(null);
  const [isEvaluated, setIsEvaluated] = useState(false);
  const [cooledDown, setCooledDown] = useState(false);
  const [pendingContents, setPendingContents] = useState([]);
  const [aproxEvalTime, setAproxEvalTime] = useState(null);
  const [testTime, setTestTime] = useState(null);
  const [eligibleForTest, setEligibleForTest] = useState(false);
  const [dialogContent, setDialogContent] = useState({});
  const [firstRender, setFirstRender] = useState(true);
  const [screenNumber, setScreenNumber] = useState(0);
  const [isExpanded, setIsExpanded] = useState(false);
  const [progress, setProgress] = useState("0%");
  const [isLoading, setIsLoading] = useState(true);

  const screenWidth = useScreenWidth();
  const dispatch = useDispatch();

  useEffect(() => {
    const fetchTestEligibility = async () => {
      setIsLoading(true);

      const response = await apis.getTestEligibility({
        courseId: courseInfo?._id,
        stageId: testInfo?.stageId,
      });
      if (response?.data?.success === 200) {
        if (!response?.data?.result?.testTaken) {
          setEligibilityCrietria([
            {
              status: response?.data?.result?.stageCompleted,
              loading: false,
              data: "All stage content completed",
            },
          ]);
          setPrevResult(null);
          setEligibleForTest(response?.data?.result?.stageCompleted);
          setPendingContents(response?.data?.result?.pendingContents ?? []);
        } else {
          setTestTime(response?.data?.result?.testTime ?? null);
          setIsEvaluated(response?.data?.result?.submissionEvaluated);

          if (!response?.data?.result?.submissionEvaluated) {
            setAproxEvalTime(response?.data?.result?.aproxEvalTime);
          }
          setPrevResult({
            totalScore: response?.data?.result?.total,
            obtainedScore: response?.data?.result?.score,
            attempts: response?.data?.result?.attempts,
          });

          const scorePercentage =
            response?.data?.result?.score / response?.data?.result?.total;

          if (scorePercentage > 0.5) {
            setEligibilityCrietria([
              // {
              //   status: true,
              //   loading: false,
              //   data: "You have completed the test",
              // },
            ]);
            setEligibleForTest(false);
          } else {
            if (response?.data?.result?.attempts >= 3) {
              setEligibleForTest(false);
              setEligibilityCrietria([
                // {
                //   status: false,
                //   loading: false,
                //   data: "You have reached the test attempt limit. If you think this is a mistake, please contact support",
                // },
              ]);
              setEligibleForTest(false);
            } else {
              setEligibilityCrietria([
                {
                  status: response?.data?.result?.stageCompleted,
                  loading: false,
                  data: "All stage content completed",
                },
              ]);
              setCooledDown(response?.data?.result?.testCooledDown);
              setEligibleForTest(
                response?.data?.result?.testCooledDown &&
                  response?.data?.result?.stageCompleted
              );
              setTestTime(response?.data?.result?.testTime);
            }
          }
        }
      } else if (response?.data?.success === 404) {
        setEligibilityCrietria([
          {
            status: false,
            loading: false,
            data: "You are not enrolled into the course!",
          },
        ]);
      } else {
        setEligibilityCrietria([
          {
            status: false,
            loading: false,
            data: "Something went wrong in fetching your eligibility, please contact support!",
          },
        ]);
      }
      setIsLoading(false);
    };

    fetchTestEligibility();
  }, [testInfo]);

  const fetchEnrollToTest = async () => {
    // check if in phone screen
    if (screenWidth && screenWidth < 641) {
      dispatch(
        services.warningAlert("Please use laptop for attending the test")
      );
      closeHandler();
      return;
    } else {
      if (eligibleForTest) {
        setIsLoading(true);
        const sheduleId = jwtDecode(testInfo?.testLink.split("sid=")[1]);
        const response = await apis.enrollStudentToTest({
          courseId: courseInfo?._id,
          scheduleId: sheduleId.id,
        });
        if (response?.status === 200) {
          dispatch(services.toggleTestLinkModal(false));
          window.open("https://www.testportal.cipherschools.com/", "_blank");
        }
      }
      setIsLoading(false);
    }
  };

  useEffect(() => {
    setDialogContent(content[0]);
    if (screenNumber === 1) {
      setProgress("50%");
    }
  }, [screenNumber]);

  const toggleReadMore = () => {
    setIsExpanded((prev) => !prev);
  };

  const handleNextClick = () => {
    if (screenNumber < maxScreen) {
      setScreenNumber(screenNumber + 1);
    }
  };

  const handlePreviousClick = () => {
    if (screenNumber > 0) {
      setScreenNumber(screenNumber - 1);
    }
  };

  const getScreens = () => {
    const stripHtmlTags = (html) => html.replace(/<\/?[^>]+(>|$)/g, "");

    const plainTextDescription = stripHtmlTags(
      dialogContent?.description || ""
    );

    // if (prevResult && prevResult.attempts === 2) {
    //   return [
    //     <WarningScreen
    //       key="warning"
    //       marksStats={marksStats}
    //       closeHandler={closeHandler}
    //     />,
    //     <section key="content" className={styles["first-screen"]}>
    //       <div className={styles["img-container"]}>
    //         <Image
    //           src={assets.Test}
    //           alt="test-image"
    //           width={500}
    //           height={500}
    //         />
    //       </div>
    //       <div className={styles["sub-container"]}>
    //         <div className={styles.test}>
    //           <h2 className={styles.title}>{dialogContent?.title}</h2>
    //           <p className={styles.description}>
    //             {isExpanded
    //               ? plainTextDescription
    //               : `${plainTextDescription?.slice(0, 325)}...`}
    //             {plainTextDescription?.length > 325 && (
    //               <span onClick={toggleReadMore}>
    //                 <strong> {isExpanded ? " Read Less" : "Read More"}</strong>
    //               </span>
    //             )}
    //           </p>
    //         </div>
    //         <div className={styles["about-platform"]}>
    //           <h2 className={styles.title}>About the Test Platform</h2>
    //           <ul>
    //             {aboutTestPlatform.map((item) => (
    //               <li key={item.id}>{item.point}</li>
    //             ))}
    //           </ul>
    //         </div>
    //       </div>
    //     </section>,
    //     <SecondScreen
    //       key="second"
    //       isEvaluated={isEvaluated}
    //       eligibilityCrietria={eligibilityCrietria}
    //       firstRender={firstRender}
    //       setFirstRender={setFirstRender}
    //       isLoading={isLoading}
    //       prevResult={prevResult}
    //       aproxEvalTime={aproxEvalTime}
    //     />,
    //   ];
    // } else {
    return [
      <section key="first" className={styles["first-screen"]}>
        <div className={styles["img-container"]}>
          <Image src={assets.Test} alt="test-image" width={500} height={500} />
        </div>
        <div className={styles["sub-container"]}>
          <div className={styles.test}>
            <h2 className={styles.title}>{dialogContent?.title}</h2>
            <p className={styles.description}>
              {isExpanded
                ? plainTextDescription
                : `${plainTextDescription?.slice(0, 325)}...`}
              {plainTextDescription?.length > 325 && (
                <span onClick={toggleReadMore}>
                  {isExpanded ? " Read Less" : "Read More"}
                </span>
              )}
            </p>
          </div>
          <div className={styles["about-platform"]}>
            <h2 className={styles.title}>About the Test Platform</h2>
            <ul>
              {aboutTestPlatform.map((item) => (
                <li key={item.id}>{item.point}</li>
              ))}
            </ul>
          </div>
        </div>
      </section>,
      <SecondScreen
        key="second"
        eligibilityCrietria={eligibilityCrietria}
        firstRender={firstRender}
        setFirstRender={setFirstRender}
        isLoading={isLoading}
        prevResult={prevResult}
        isEvaluated={isEvaluated}
        aproxEvalTime={aproxEvalTime}
        pendingContents={pendingContents}
        testTime={testTime}
        cooledDown={cooledDown}
      />,
    ];
    // }
  };

  const screens = getScreens();
  const maxScreen = screens.length - 1;

  useEffect(() => {
    setProgress(`${(screenNumber / maxScreen) * 100}%`);
  }, [screenNumber, maxScreen]);

  return (
    <section className={styles.container}>
      <article className={styles.contentContainer}>
        {screens[screenNumber]}
      </article>

      <div className={styles["footer"]}>
        <div className={styles["progress-container"]}>
          <div
            className={styles["progressbar"]}
            style={{ width: progress }}
          ></div>
        </div>

        <div className={styles["btn-container"]}>
          {screenNumber === 0 ? (
            <>
              <Components.Button
                className={styles.closeBtn}
                dis
                name={"Close"}
                handleClick={closeHandler}
              />
              <Components.Button
                className={styles.btn}
                name={"Next"}
                handleClick={handleNextClick}
              />
            </>
          ) : screenNumber === 1 ? (
            <>
              {/* {attemptsCount === 2 && (
                <>
                  <Components.Button
                    className={styles["prev-btn"]}
                    name={"Previous"}
                    disable={isLoading}
                    handleClick={handlePreviousClick}
                  />

                  <Components.Button
                    className={styles.btn}
                    name={"Next"}
                    handleClick={handleNextClick}
                  />
                </>
              )} */}
              {/* {attemptsCount !== 2 && ( */}
              <>
                <Components.Button
                  className={styles["prev-btn"]}
                  name={"Previous"}
                  disable={isLoading}
                  handleClick={handlePreviousClick}
                />
                <Components.Button
                  className={styles.btn}
                  disable={isLoading && eligibleForTest}
                  name={
                    eligibleForTest
                      ? "Start Test"
                      : pendingContents.length > 0
                        ? "Continue Learning"
                        : "Close"
                  }
                  handleClick={() => {
                    eligibleForTest ? fetchEnrollToTest() : closeHandler();
                  }}
                  icon={
                    isLoading && (
                      <Icon
                        icon="line-md:loading-loop"
                        className={styles.loadingIcon}
                      />
                    )
                  }
                />
              </>
              {/* // )} */}
            </>
          ) : (
            <>
              <Components.Button
                className={styles["prev-btn"]}
                name={"Previous"}
                disable={isLoading}
                handleClick={handlePreviousClick}
              />

              <Components.Button
                className={styles.btn}
                dis
                disable={isLoading}
                name={eligibleForTest ? "Start Test" : "Close"}
                handleClick={() => {
                  eligibleForTest ? fetchEnrollToTest() : closeHandler();
                }}
                icon={
                  isLoading && (
                    <Icon
                      icon="line-md:loading-loop"
                      className={styles.loadingIcon}
                    />
                  )
                }
              />
            </>
          )}
        </div>
      </div>
    </section>
  );
}
export default TestLinkModals;
